import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export type ImagePricingEntry = {
  id: string;
  label: string;
  family: string; // worker aile enum'u büyüdü — string olarak kabul et
  model: string;
  size?: '1024x1024' | '1024x1792' | '1792x1024';
  quality?: 'standard' | 'hd';
  note?: string;
  credits: number;
  cost_micro_usd: number;
  deprecated?: boolean;
};

export function useImagePricing() {
  return useQuery({
    queryKey: ['images', 'pricing'],
    queryFn: () => api.get<{ pricing: ImagePricingEntry[] }>('/api/app/images/pricing'),
    staleTime: 10 * 60 * 1000,
  });
}

export function useGenerateImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      content_id: string;
      prompt?: string;
      use_template?: boolean;
      pricing_id?: string;
      size?: '1024x1024' | '1024x1792' | '1792x1024';
      quality?: 'standard' | 'hd';
      alt_text?: string;
    }) =>
      api.post<{
        r2_key: string;
        public_url: string;
        prompt: string;
        revised_prompt?: string;
        credits_consumed: number;
        credit_balance: number;
      }>('/api/app/images/generate', body),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['content', 'detail', vars.content_id] });
      qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export function useRemoveImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (content_id: string) =>
      api.del<{ removed: boolean }>(`/api/app/images/${content_id}`),
    onSuccess: (_, content_id) => {
      qc.invalidateQueries({ queryKey: ['content', 'detail', content_id] });
    },
  });
}

// ---------- Body images (per-section images in the article body) ----------

export type BodyImage = {
  id: string;
  r2_key: string;
  public_url: string;
  alt_text: string | null;
  prompt: string | null;
  position_index: number;
  section_title: string | null;
  model: string | null;
  credits_consumed: number | null;
  created_at: number;
};

export function useBodyImages(contentId: string | null | undefined) {
  return useQuery({
    queryKey: ['content', 'body-images', contentId],
    queryFn: () => api.get<{ images: BodyImage[] }>(`/api/app/images/content/${contentId}/body`),
    enabled: !!contentId,
    staleTime: 60 * 1000,
  });
}

export function useAddBodyImages(contentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      body_count: number;
      body_layout: 'full_width' | 'float_left' | 'float_right' | 'alternating';
      pricing_id?: string;
    }) =>
      api.post<{
        images: Array<{ position_type: string; r2_key: string; alt_text: string }>;
        credits_consumed: number;
      }>(`/api/app/images/content/${contentId}/body`, args),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['content', 'body-images', contentId] });
      qc.invalidateQueries({ queryKey: ['content', 'detail', contentId] });
      qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export function useRegenerateBodyImage(contentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      image_id: string;
      prompt?: string;
      pricing_id?: string;
      alt_text?: string;
    }) =>
      api.post<{
        id: string;
        r2_key: string;
        public_url: string;
        prompt: string;
        credits_consumed: number;
        credit_balance: number;
      }>(`/api/app/images/body/${args.image_id}/regenerate`, {
        prompt: args.prompt,
        pricing_id: args.pricing_id,
        alt_text: args.alt_text,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['content', 'body-images', contentId] });
      qc.invalidateQueries({ queryKey: ['content', 'detail', contentId] });
      qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}
